export interface Problem {
  /** URL-safe unique id, e.g. "sleepIn" */
  id: string;
  /** Function name the user must define */
  fn: string;
  /** Parameter names, used to build starter code */
  params: string[];
  /** Problem statement (plain text; `backticks` render as code) */
  description: string;
  /** Reference solution source (JavaScript) */
  solution: string;
  /** Test inputs; expected values are computed from the solution */
  tests: unknown[][];
}

export interface Category {
  id: string;
  name: string;
  level: 1 | 2 | 3;
  blurb: string;
  problems: Problem[];
}

const warmup1: Problem[] = [
  {
    id: 'sleepIn',
    fn: 'sleepIn',
    params: ['weekday', 'vacation'],
    description:
      'The parameter `weekday` is true if it is a weekday, and the parameter `vacation` is true if we are on vacation. We sleep in if it is not a weekday or we\'re on vacation. Return true if we sleep in.',
    solution: `function sleepIn(weekday, vacation) {
  return !weekday || vacation;
}`,
    tests: [[false, false], [true, false], [false, true], [true, true]],
  },
  {
    id: 'monkeyTrouble',
    fn: 'monkeyTrouble',
    params: ['aSmile', 'bSmile'],
    description:
      'We have two monkeys, a and b, and the parameters `aSmile` and `bSmile` indicate if each is smiling. We are in trouble if they are both smiling or if neither of them is smiling. Return true if we are in trouble.',
    solution: `function monkeyTrouble(aSmile, bSmile) {
  return aSmile === bSmile;
}`,
    tests: [[true, true], [false, false], [true, false], [false, true]],
  },
  {
    id: 'sumDouble',
    fn: 'sumDouble',
    params: ['a', 'b'],
    description: 'Given two numbers, return their sum. Unless the two values are the same, then return double their sum.',
    solution: `function sumDouble(a, b) {
  const sum = a + b;
  return a === b ? sum * 2 : sum;
}`,
    tests: [[1, 2], [3, 2], [2, 2], [-1, 0], [3, 3], [0, 0], [0, 1], [3, 4]],
  },
  {
    id: 'diff21',
    fn: 'diff21',
    params: ['n'],
    description: 'Given a number `n`, return the absolute difference between `n` and 21, except return double the absolute difference if `n` is over 21.',
    solution: `function diff21(n) {
  return n > 21 ? (n - 21) * 2 : 21 - n;
}`,
    tests: [[19], [10], [21], [22], [25], [30], [0], [1], [2], [-1], [-2], [50]],
  },
  {
    id: 'parrotTrouble',
    fn: 'parrotTrouble',
    params: ['talking', 'hour'],
    description:
      'We have a loud talking parrot. The `hour` parameter is the current hour time in the range 0..23. We are in trouble if the parrot is talking and the hour is before 7 or after 20. Return true if we are in trouble.',
    solution: `function parrotTrouble(talking, hour) {
  return talking && (hour < 7 || hour > 20);
}`,
    tests: [[true, 6], [true, 7], [false, 6], [true, 21], [false, 21], [false, 20], [true, 23], [false, 23], [true, 20], [false, 12]],
  },
  {
    id: 'makes10',
    fn: 'makes10',
    params: ['a', 'b'],
    description: 'Given two numbers, `a` and `b`, return true if one of them is 10 or if their sum is 10.',
    solution: `function makes10(a, b) {
  return a === 10 || b === 10 || a + b === 10;
}`,
    tests: [[9, 10], [9, 9], [1, 9], [10, 1], [10, 10], [8, 2], [8, 3], [10, 42], [12, -2]],
  },
  {
    id: 'nearHundred',
    fn: 'nearHundred',
    params: ['n'],
    description: 'Given a number `n`, return true if it is within 10 of 100 or 200. Note: `Math.abs(num)` computes the absolute value of a number.',
    solution: `function nearHundred(n) {
  return Math.abs(100 - n) <= 10 || Math.abs(200 - n) <= 10;
}`,
    tests: [[93], [90], [89], [110], [111], [121], [-101], [-209], [190], [209], [0], [5], [-50], [191], [189], [200], [210], [211], [290]],
  },
  {
    id: 'posNeg',
    fn: 'posNeg',
    params: ['a', 'b', 'negative'],
    description: 'Given two numbers, return true if one is negative and one is positive. Except if the parameter `negative` is true, then return true only if both are negative.',
    solution: `function posNeg(a, b, negative) {
  if (negative) return a < 0 && b < 0;
  return (a < 0 && b > 0) || (a > 0 && b < 0);
}`,
    tests: [[1, -1, false], [-1, 1, false], [-4, -5, true], [-4, -5, false], [-4, 5, false], [-4, 5, true], [1, 1, false], [-1, -1, false], [1, -1, true], [-1, 1, true], [1, 1, true], [-1, -1, true], [5, -5, false], [-6, 6, false], [-5, -6, false], [-2, -1, false], [1, 2, false], [-5, 6, true], [-5, -5, true]],
  },
  {
    id: 'notString',
    fn: 'notString',
    params: ['str'],
    description: 'Given a string, return a new string where "not " has been added to the front. However, if the string already begins with "not", return the string unchanged.',
    solution: `function notString(str) {
  if (str.startsWith('not')) return str;
  return 'not ' + str;
}`,
    tests: [['candy'], ['x'], ['not bad'], ['bad'], ['not'], ['is not'], ['no']],
  },
  {
    id: 'missingChar',
    fn: 'missingChar',
    params: ['str', 'n'],
    description: 'Given a non-empty string and a number `n`, return a new string where the char at index `n` has been removed. The value of `n` will be a valid index of a char in the original string (i.e. `n` will be in the range 0..str.length-1 inclusive).',
    solution: `function missingChar(str, n) {
  return str.slice(0, n) + str.slice(n + 1);
}`,
    tests: [['kitten', 1], ['kitten', 0], ['kitten', 4], ['Hi', 0], ['Hi', 1], ['code', 0], ['code', 1], ['code', 2], ['code', 3], ['chocolate', 8]],
  },
  {
    id: 'frontBack',
    fn: 'frontBack',
    params: ['str'],
    description: 'Given a string, return a new string where the first and last chars have been exchanged.',
    solution: `function frontBack(str) {
  if (str.length <= 1) return str;
  return str[str.length - 1] + str.slice(1, -1) + str[0];
}`,
    tests: [['code'], ['a'], ['ab'], ['abc'], [''], ['Chocolate'], ['aavJ'], ['hello']],
  },
  {
    id: 'front3',
    fn: 'front3',
    params: ['str'],
    description: 'Given a string, we\'ll say that the front is the first 3 chars of the string. If the string length is less than 3, the front is whatever is there. Return a new string which is 3 copies of the front.',
    solution: `function front3(str) {
  const front = str.slice(0, 3);
  return front + front + front;
}`,
    tests: [['JavaScript'], ['Chocolate'], ['abc'], ['abcXYZ'], ['ab'], ['a'], ['']],
  },
  {
    id: 'backAround',
    fn: 'backAround',
    params: ['str'],
    description: 'Given a string, take the last char and return a new string with the last char added at the front and back, so "cat" yields "tcatt". The original string will be length 1 or more.',
    solution: `function backAround(str) {
  const last = str[str.length - 1];
  return last + str + last;
}`,
    tests: [['cat'], ['Hello'], ['a'], ['abc'], ['read'], ['boo']],
  },
  {
    id: 'or35',
    fn: 'or35',
    params: ['n'],
    description: 'Return true if the given non-negative number is a multiple of 3 or a multiple of 5. Use the `%` "mod" operator.',
    solution: `function or35(n) {
  return n % 3 === 0 || n % 5 === 0;
}`,
    tests: [[3], [10], [8], [15], [5], [4], [9], [4], [7], [6], [17], [18], [29], [20], [21], [22], [45], [99]],
  },
  {
    id: 'icyHot',
    fn: 'icyHot',
    params: ['temp1', 'temp2'],
    description: 'Given two temperatures, return true if one is less than 0 and the other is greater than 100.',
    solution: `function icyHot(temp1, temp2) {
  return (temp1 < 0 && temp2 > 100) || (temp2 < 0 && temp1 > 100);
}`,
    tests: [[120, -1], [-1, 120], [2, 120], [-1, 100], [-2, -2], [120, 120]],
  },
  {
    id: 'in1020',
    fn: 'in1020',
    params: ['a', 'b'],
    description: 'Given two numbers, return true if either of them is in the range 10..20 inclusive.',
    solution: `function in1020(a, b) {
  const inRange = (n) => n >= 10 && n <= 20;
  return inRange(a) || inRange(b);
}`,
    tests: [[12, 99], [21, 12], [8, 99], [99, 10], [20, 20], [21, 21], [9, 9]],
  },
  {
    id: 'startHi',
    fn: 'startHi',
    params: ['str'],
    description: 'Given a string, return true if the string starts with "hi" and false otherwise.',
    solution: `function startHi(str) {
  return str.startsWith('hi');
}`,
    tests: [['hi there'], ['hi'], ['hello hi'], ['he'], ['h'], [''], ['ho hi'], ['hi ho']],
  },
  {
    id: 'max1020',
    fn: 'max1020',
    params: ['a', 'b'],
    description: 'Given 2 positive numbers, return the larger value that is in the range 10..20 inclusive, or return 0 if neither is in that range.',
    solution: `function max1020(a, b) {
  const ok = (n) => n >= 10 && n <= 20;
  if (ok(a) && ok(b)) return Math.max(a, b);
  if (ok(a)) return a;
  if (ok(b)) return b;
  return 0;
}`,
    tests: [[11, 19], [19, 11], [11, 9], [9, 21], [10, 21], [21, 10], [9, 11], [23, 10], [20, 10], [7, 20], [17, 16]],
  },
];

const string1: Problem[] = [
  {
    id: 'helloName',
    fn: 'helloName',
    params: ['name'],
    description: 'Given a string `name`, e.g. "Bob", return a greeting of the form "Hello Bob!".',
    solution: `function helloName(name) {
  return \`Hello \${name}!\`;
}`,
    tests: [['Bob'], ['Alice'], ['X'], ['Dolly'], ['Alpha'], ['Omega'], ['Goodbye'], ['ho ho ho'], ['xyz!'], ['Hello']],
  },
  {
    id: 'makeAbba',
    fn: 'makeAbba',
    params: ['a', 'b'],
    description: 'Given two strings, `a` and `b`, return the result of putting them together in the order abba, e.g. "Hi" and "Bye" returns "HiByeByeHi".',
    solution: `function makeAbba(a, b) {
  return a + b + b + a;
}`,
    tests: [['Hi', 'Bye'], ['Yo', 'Alice'], ['What', 'Up'], ['aaa', 'bbb'], ['x', 'y'], ['x', ''], ['', 'y'], ['Bo', 'Ya']],
  },
  {
    id: 'makeTags',
    fn: 'makeTags',
    params: ['tag', 'word'],
    description: 'The web is built with HTML strings like "<i>Yay</i>" which draws Yay as italic text. In this example, the "i" tag makes <i> and </i> which surround the word "Yay". Given tag and word strings, create the HTML string with tags around the word, e.g. "<i>Yay</i>".',
    solution: `function makeTags(tag, word) {
  return \`<\${tag}>\${word}</\${tag}>\`;
}`,
    tests: [['i', 'Yay'], ['i', 'Hello'], ['cite', 'Yay'], ['address', 'here'], ['body', 'Heart'], ['i', 'i'], ['i', '']],
  },
  {
    id: 'extraEnd',
    fn: 'extraEnd',
    params: ['str'],
    description: 'Given a string, return a new string made of 3 copies of the last 2 chars of the original string. The string length will be at least 2.',
    solution: `function extraEnd(str) {
  return str.slice(-2).repeat(3);
}`,
    tests: [['Hello'], ['ab'], ['Hi'], ['Candy'], ['Code']],
  },
  {
    id: 'firstHalf',
    fn: 'firstHalf',
    params: ['str'],
    description: 'Given a string of even length, return the first half. So the string "WooHoo" yields "Woo".',
    solution: `function firstHalf(str) {
  return str.slice(0, str.length / 2);
}`,
    tests: [['WooHoo'], ['HelloThere'], ['abcdef'], ['ab'], [''], ['0123456789'], ['kitten']],
  },
  {
    id: 'withoutEnd',
    fn: 'withoutEnd',
    params: ['str'],
    description: 'Given a string, return a version without the first and last char, so "Hello" yields "ell". The string length will be at least 2.',
    solution: `function withoutEnd(str) {
  return str.slice(1, -1);
}`,
    tests: [['Hello'], ['JavaScript'], ['coding'], ['code'], ['ab'], ['Chocolate!'], ['kitten'], ['woohoo']],
  },
  {
    id: 'comboString',
    fn: 'comboString',
    params: ['a', 'b'],
    description: 'Given 2 strings, `a` and `b`, return a string of the form short+long+short, with the shorter string on the outside and the longer string on the inside. The strings will not be the same length, but they may be empty (length 0).',
    solution: `function comboString(a, b) {
  return a.length < b.length ? a + b + a : b + a + b;
}`,
    tests: [['Hello', 'hi'], ['hi', 'Hello'], ['aaa', 'b'], ['b', 'aaa'], ['aaa', ''], ['', 'bb'], ['aaa', '1234'], ['bb', 'a']],
  },
  {
    id: 'left2',
    fn: 'left2',
    params: ['str'],
    description: 'Given a string, return a "rotated left 2" version where the first 2 chars are moved to the end. The string length will be at least 2.',
    solution: `function left2(str) {
  return str.slice(2) + str.slice(0, 2);
}`,
    tests: [['Hello'], ['js'], ['Hi'], ['code'], ['cat'], ['12345'], ['Chocolate'], ['bricks']],
  },
  {
    id: 'middleTwo',
    fn: 'middleTwo',
    params: ['str'],
    description: 'Given a string of even length, return a string made of the middle two chars, so the string "string" yields "ri". The string length will be at least 2.',
    solution: `function middleTwo(str) {
  const mid = str.length / 2;
  return str.slice(mid - 1, mid + 1);
}`,
    tests: [['string'], ['code'], ['Practice'], ['ab'], ['0123456789']],
  },
  {
    id: 'endsLy',
    fn: 'endsLy',
    params: ['str'],
    description: 'Given a string, return true if it ends in "ly".',
    solution: `function endsLy(str) {
  return str.endsWith('ly');
}`,
    tests: [['oddly'], ['y'], ['oddy'], ['oddl'], ['olydd'], ['ly'], [''], ['falsely'], ['evenly']],
  },
  {
    id: 'nTwice',
    fn: 'nTwice',
    params: ['str', 'n'],
    description: 'Given a string and a number `n`, return a string made of the first and last `n` chars from the string. The string length will be at least `n`.',
    solution: `function nTwice(str, n) {
  return str.slice(0, n) + str.slice(str.length - n);
}`,
    tests: [['Hello', 2], ['Chocolate', 3], ['Chocolate', 1], ['Chocolate', 0], ['Hello', 4], ['Code', 4], ['Code', 2]],
  },
  {
    id: 'hasBad',
    fn: 'hasBad',
    params: ['str'],
    description: 'Given a string, return true if "bad" appears starting at index 0 or 1 in the string, such as with "badxxx" or "xbadxx" but not "xxbadxx". The string may be any length, including 0.',
    solution: `function hasBad(str) {
  return str.startsWith('bad') || str.startsWith('bad', 1);
}`,
    tests: [['badxx'], ['xbadxx'], ['xxbadxx'], ['code'], ['bad'], ['ba'], ['xba'], ['xbad'], [''], ['badyy']],
  },
  {
    id: 'lastChars',
    fn: 'lastChars',
    params: ['a', 'b'],
    description: 'Given 2 strings, `a` and `b`, return a new string made of the first char of `a` and the last char of `b`, so "yo" and "java" yields "ya". If either string is length 0, use "@" for its missing char.',
    solution: `function lastChars(a, b) {
  const first = a.length > 0 ? a[0] : '@';
  const last = b.length > 0 ? b[b.length - 1] : '@';
  return first + last;
}`,
    tests: [['last', 'chars'], ['yo', 'java'], ['hi', ''], ['', 'hello'], ['', ''], ['kitten', 'hi'], ['k', 'zip'], ['kitten', ''], ['kitten', 'zip']],
  },
  {
    id: 'deFront',
    fn: 'deFront',
    params: ['str'],
    description: 'Given a string, return a version without the first 2 chars. Except keep the first char if it is "a" and keep the second char if it is "b". The string may be any length.',
    solution: `function deFront(str) {
  let result = '';
  if (str.length > 0 && str[0] === 'a') result += 'a';
  if (str.length > 1 && str[1] === 'b') result += 'b';
  return result + str.slice(2);
}`,
    tests: [['Hello'], ['java'], ['away'], ['axy'], ['abc'], ['xby'], ['ab'], ['ax'], ['axb'], ['aaa'], ['xbc'], ['bbb'], ['bazz'], ['ba'], ['abxyz'], ['hi'], ['his'], ['xz'], ['zzz']],
  },
];

const array1: Problem[] = [
  {
    id: 'firstLast6',
    fn: 'firstLast6',
    params: ['nums'],
    description: 'Given an array of numbers, return true if 6 appears as either the first or last element in the array. The array will be length 1 or more.',
    solution: `function firstLast6(nums) {
  return nums[0] === 6 || nums[nums.length - 1] === 6;
}`,
    tests: [[[1, 2, 6]], [[6, 1, 2, 3]], [[13, 6, 1, 2, 3]], [[13, 6, 1, 2, 6]], [[3, 2, 1]], [[3, 6, 1]], [[3, 6]], [[6]], [[3]], [[5, 6]], [[5, 5]], [[1, 2, 3, 4, 6]], [[1, 2, 3, 4]]],
  },
  {
    id: 'sameFirstLast',
    fn: 'sameFirstLast',
    params: ['nums'],
    description: 'Given an array of numbers, return true if the array is length 1 or more, and the first element and the last element are equal.',
    solution: `function sameFirstLast(nums) {
  return nums.length >= 1 && nums[0] === nums[nums.length - 1];
}`,
    tests: [[[1, 2, 3]], [[1, 2, 3, 1]], [[1, 2, 1]], [[7]], [[]], [[1, 2, 3, 4, 5, 1]], [[1, 2, 3, 4, 5, 13]], [[13, 2, 3, 4, 5, 13]], [[7, 7]]],
  },
  {
    id: 'makePi',
    fn: 'makePi',
    params: [],
    description: 'Return an array containing the first 3 digits of pi, `[3, 1, 4]`.',
    solution: `function makePi() {
  return [3, 1, 4];
}`,
    tests: [[]],
  },
  {
    id: 'commonEnd',
    fn: 'commonEnd',
    params: ['a', 'b'],
    description: 'Given 2 arrays of numbers, `a` and `b`, return true if they have the same first element or they have the same last element. Both arrays will be length 1 or more.',
    solution: `function commonEnd(a, b) {
  return a[0] === b[0] || a[a.length - 1] === b[b.length - 1];
}`,
    tests: [[[1, 2, 3], [7, 3]], [[1, 2, 3], [7, 3, 2]], [[1, 2, 3], [1, 3]], [[1, 2, 3], [1]], [[1, 2, 3], [2]]],
  },
  {
    id: 'sum3',
    fn: 'sum3',
    params: ['nums'],
    description: 'Given an array of numbers length 3, return the sum of all the elements.',
    solution: `function sum3(nums) {
  return nums[0] + nums[1] + nums[2];
}`,
    tests: [[[1, 2, 3]], [[5, 11, 2]], [[7, 0, 0]], [[1, 2, 1]], [[1, 1, 1]], [[2, 7, 2]]],
  },
  {
    id: 'rotateLeft3',
    fn: 'rotateLeft3',
    params: ['nums'],
    description: 'Given an array of numbers length 3, return an array with the elements "rotated left" so `[1, 2, 3]` yields `[2, 3, 1]`.',
    solution: `function rotateLeft3(nums) {
  return [nums[1], nums[2], nums[0]];
}`,
    tests: [[[1, 2, 3]], [[5, 11, 9]], [[7, 0, 0]], [[1, 2, 1]], [[0, 0, 1]]],
  },
  {
    id: 'reverse3',
    fn: 'reverse3',
    params: ['nums'],
    description: 'Given an array of numbers length 3, return a new array with the elements in reverse order, so `[1, 2, 3]` becomes `[3, 2, 1]`.',
    solution: `function reverse3(nums) {
  return [nums[2], nums[1], nums[0]];
}`,
    tests: [[[1, 2, 3]], [[5, 11, 9]], [[7, 0, 0]], [[2, 1, 2]], [[1, 2, 1]], [[2, 11, 3]], [[0, 6, 5]], [[7, 2, 3]]],
  },
  {
    id: 'maxEnd3',
    fn: 'maxEnd3',
    params: ['nums'],
    description: 'Given an array of numbers length 3, figure out which is larger, the first or last element in the array, and set all the other elements to be that value. Return the changed array.',
    solution: `function maxEnd3(nums) {
  const max = Math.max(nums[0], nums[2]);
  return [max, max, max];
}`,
    tests: [[[1, 2, 3]], [[11, 5, 9]], [[2, 11, 3]], [[11, 3, 3]], [[3, 11, 11]], [[2, 2, 2]], [[2, 11, 2]], [[0, 0, 1]]],
  },
  {
    id: 'sum2',
    fn: 'sum2',
    params: ['nums'],
    description: 'Given an array of numbers, return the sum of the first 2 elements in the array. If the array length is less than 2, just sum up the elements that exist, returning 0 if the array is length 0.',
    solution: `function sum2(nums) {
  return nums.slice(0, 2).reduce((a, b) => a + b, 0);
}`,
    tests: [[[1, 2, 3]], [[1, 1]], [[1, 1, 1, 1]], [[1, 2]], [[1]], [[]], [[4, 5, 6]], [[4]]],
  },
  {
    id: 'middleWay',
    fn: 'middleWay',
    params: ['a', 'b'],
    description: 'Given 2 arrays of numbers, `a` and `b`, each length 3, return a new array length 2 containing their middle elements.',
    solution: `function middleWay(a, b) {
  return [a[1], b[1]];
}`,
    tests: [[[1, 2, 3], [4, 5, 6]], [[7, 7, 7], [3, 8, 0]], [[5, 2, 9], [1, 4, 5]], [[1, 9, 7], [4, 8, 8]], [[1, 2, 3], [3, 1, 4]], [[1, 2, 3], [4, 1, 1]]],
  },
  {
    id: 'has23',
    fn: 'has23',
    params: ['nums'],
    description: 'Given an array of numbers length 2, return true if it contains a 2 or a 3.',
    solution: `function has23(nums) {
  return nums.includes(2) || nums.includes(3);
}`,
    tests: [[[2, 5]], [[4, 3]], [[4, 5]], [[2, 2]], [[3, 2]], [[3, 3]], [[7, 7]], [[3, 9]], [[9, 5]]],
  },
  {
    id: 'makeMiddle',
    fn: 'makeMiddle',
    params: ['nums'],
    description: 'Given an array of numbers of even length, return a new array length 2 containing the middle two elements from the original array. The original array will be length 2 or more.',
    solution: `function makeMiddle(nums) {
  const mid = nums.length / 2;
  return [nums[mid - 1], nums[mid]];
}`,
    tests: [[[1, 2, 3, 4]], [[7, 1, 2, 3, 4, 9]], [[1, 2]], [[5, 2, 4, 7]], [[9, 0, 4, 3, 9, 1]]],
  },
  {
    id: 'frontPiece',
    fn: 'frontPiece',
    params: ['nums'],
    description: 'Given an array of numbers, return a new array length 2 containing the first two elements from the original array. The original array may be shorter than 2, in which case use whatever elements are present.',
    solution: `function frontPiece(nums) {
  return nums.slice(0, 2);
}`,
    tests: [[[1, 2, 3]], [[1, 2]], [[1]], [[]], [[6, 5, 0]], [[6, 5]], [[3, 1, 4, 1, 5]], [[6]]],
  },
];

const logic1: Problem[] = [
  {
    id: 'squirrelPlay',
    fn: 'squirrelPlay',
    params: ['temp', 'isSummer'],
    description: 'The squirrels in Palo Alto spend most of the day playing. In particular, they play if the temperature is between 60 and 90 (inclusive). Unless it is summer, then the upper limit is 100 instead of 90. Given a number temperature and a boolean `isSummer`, return true if the squirrels play and false otherwise.',
    solution: `function squirrelPlay(temp, isSummer) {
  const upper = isSummer ? 100 : 90;
  return temp >= 60 && temp <= upper;
}`,
    tests: [[70, false], [95, false], [95, true], [90, false], [90, true], [50, false], [50, true], [100, false], [100, true], [105, true], [59, false], [59, true], [60, false]],
  },
  {
    id: 'dateFashion',
    fn: 'dateFashion',
    params: ['you', 'date'],
    description: 'You and your date are trying to get a table at a restaurant. The parameter `you` is the stylishness of your clothes, in the range 0..10, and `date` is the stylishness of your date\'s clothes. The result getting the table is encoded as 0 = no, 1 = maybe, 2 = yes. If either of you is very stylish, 8 or more, then the result is 2 (yes). With the exception that if either of you has style of 2 or less, then the result is 0 (no). Otherwise the result is 1 (maybe).',
    solution: `function dateFashion(you, date) {
  if (you <= 2 || date <= 2) return 0;
  if (you >= 8 || date >= 8) return 2;
  return 1;
}`,
    tests: [[5, 10], [5, 2], [5, 5], [3, 3], [10, 2], [2, 9], [9, 9], [10, 5], [2, 2], [3, 7], [2, 7], [6, 2]],
  },
  {
    id: 'caughtSpeeding',
    fn: 'caughtSpeeding',
    params: ['speed', 'isBirthday'],
    description: 'You are driving a little too fast, and a police officer stops you. Write code to compute the result, encoded as a number value: 0 = no ticket, 1 = small ticket, 2 = big ticket. If speed is 60 or less, the result is 0. If speed is between 61 and 80 inclusive, the result is 1. If speed is 81 or more, the result is 2. Unless it is your birthday -- on that day, your speed can be 5 higher in all cases.',
    solution: `function caughtSpeeding(speed, isBirthday) {
  const bonus = isBirthday ? 5 : 0;
  if (speed <= 60 + bonus) return 0;
  if (speed <= 80 + bonus) return 1;
  return 2;
}`,
    tests: [[60, false], [65, false], [65, true], [80, false], [85, false], [85, true], [70, false], [75, false], [75, true], [40, false], [40, true], [90, false]],
  },
  {
    id: 'sortaSum',
    fn: 'sortaSum',
    params: ['a', 'b'],
    description: 'Given 2 numbers, `a` and `b`, return their sum. However, sums in the range 10..19 inclusive are forbidden, so in that case just return 20.',
    solution: `function sortaSum(a, b) {
  const sum = a + b;
  return sum >= 10 && sum <= 19 ? 20 : sum;
}`,
    tests: [[3, 4], [9, 4], [10, 11], [12, -3], [-3, 12], [4, 5], [4, 6], [14, 7]],
  },
  {
    id: 'alarmClock',
    fn: 'alarmClock',
    params: ['day', 'vacation'],
    description: 'Given a day of the week encoded as 0=Sun, 1=Mon, 2=Tue, ...6=Sat, and a boolean indicating if we are on vacation, return a string of the form "7:00" indicating when the alarm clock should ring. Weekdays, the alarm should be "7:00" and on the weekend it should be "10:00". Unless we are on vacation -- then on weekdays it should be "10:00" and weekends it should be "off".',
    solution: `function alarmClock(day, vacation) {
  const weekend = day === 0 || day === 6;
  if (vacation) return weekend ? 'off' : '10:00';
  return weekend ? '10:00' : '7:00';
}`,
    tests: [[1, false], [5, false], [0, false], [6, false], [0, true], [6, true], [1, true], [3, true], [5, true]],
  },
  {
    id: 'love6',
    fn: 'love6',
    params: ['a', 'b'],
    description: 'The number 6 is a truly great number. Given two numbers, `a` and `b`, return true if either one is 6. Or if their sum or difference is 6.',
    solution: `function love6(a, b) {
  return a === 6 || b === 6 || a + b === 6 || Math.abs(a - b) === 6;
}`,
    tests: [[6, 4], [4, 5], [1, 5], [1, 6], [1, 8], [1, 7], [7, 5], [8, 2], [6, 6], [-6, 2], [-4, -10], [-7, 1], [7, -1], [-6, 12], [-2, -4], [7, 1], [0, 9], [8, 3], [3, 3], [3, 4]],
  },
  {
    id: 'in1To10',
    fn: 'in1To10',
    params: ['n', 'outsideMode'],
    description: 'Given a number `n`, return true if it\'s in the range 1..10, inclusive. Unless `outsideMode` is true, in which case return true if the number is less or equal to 1, or greater or equal to 10.',
    solution: `function in1To10(n, outsideMode) {
  if (outsideMode) return n <= 1 || n >= 10;
  return n >= 1 && n <= 10;
}`,
    tests: [[5, false], [11, false], [11, true], [10, false], [10, true], [9, true], [1, true], [0, false], [0, true], [-1, false], [-1, true], [2, false], [-1, true]],
  },
  {
    id: 'fizzString',
    fn: 'fizzString',
    params: ['str'],
    description: 'Given a string `str`, if the string starts with "f" return "Fizz". If the string ends with "b" return "Buzz". If both the "f" and "b" conditions are true, return "FizzBuzz". In all other cases, return the string unchanged.',
    solution: `function fizzString(str) {
  const f = str.startsWith('f');
  const b = str.endsWith('b');
  if (f && b) return 'FizzBuzz';
  if (f) return 'Fizz';
  if (b) return 'Buzz';
  return str;
}`,
    tests: [['fig'], ['dib'], ['fib'], ['abc'], ['fooo'], ['booo'], ['ooob'], ['fooob'], ['f'], ['b'], ['abcb'], ['Hello'], ['Hellob'], ['af'], ['bf'], ['fob']],
  },
  {
    id: 'fizzBuzzOne',
    fn: 'fizzBuzzOne',
    params: ['n'],
    description: 'Given a number `n`, return the string form of the number followed by "!". So the number 6 yields "6!". Except if the number is divisible by 3 use "Fizz" instead of the number, and if the number is divisible by 5 use "Buzz", and if divisible by both 3 and 5, use "FizzBuzz".',
    solution: `function fizzBuzzOne(n) {
  if (n % 15 === 0) return 'FizzBuzz!';
  if (n % 3 === 0) return 'Fizz!';
  if (n % 5 === 0) return 'Buzz!';
  return n + '!';
}`,
    tests: [[1], [2], [3], [4], [5], [6], [8], [15], [16], [18], [19], [21], [44], [45], [100]],
  },
  {
    id: 'greenTicket',
    fn: 'greenTicket',
    params: ['a', 'b', 'c'],
    description: 'You have a green lottery ticket, with numbers `a`, `b`, and `c` on it. If the numbers are all different from each other, the result is 0. If all of the numbers are the same, the result is 20. If two of the numbers are the same, the result is 10.',
    solution: `function greenTicket(a, b, c) {
  if (a === b && b === c) return 20;
  if (a === b || b === c || a === c) return 10;
  return 0;
}`,
    tests: [[1, 2, 3], [2, 2, 2], [1, 1, 2], [2, 1, 1], [1, 2, 1], [3, 2, 1], [0, 0, 0], [2, 0, 0], [0, 9, 10], [0, 10, 0], [9, 9, 9], [9, 0, 9]],
  },
  {
    id: 'lastDigit',
    fn: 'lastDigit',
    params: ['a', 'b', 'c'],
    description: 'Given three non-negative numbers, return true if two or more of them have the same last digit, such as 27 and 57.',
    solution: `function lastDigit(a, b, c) {
  const x = a % 10, y = b % 10, z = c % 10;
  return x === y || y === z || x === z;
}`,
    tests: [[23, 19, 13], [23, 19, 12], [23, 19, 3], [23, 19, 39], [1, 2, 3], [1, 1, 2], [1, 2, 2], [14, 25, 43], [14, 25, 45], [248, 106, 1002], [248, 106, 1008], [10, 11, 20], [0, 11, 0]],
  },
];

const warmup2: Problem[] = [
  {
    id: 'stringTimes',
    fn: 'stringTimes',
    params: ['str', 'n'],
    description: 'Given a string and a non-negative number `n`, return a larger string that is `n` copies of the original string. Try it with a loop first, then try `str.repeat()`.',
    solution: `function stringTimes(str, n) {
  let result = '';
  for (let i = 0; i < n; i++) result += str;
  return result;
}`,
    tests: [['Hi', 2], ['Hi', 3], ['Hi', 1], ['Hi', 0], ['Hi', 5], ['Oh Boy!', 2], ['x', 4], ['', 4], ['code', 2], ['code', 3]],
  },
  {
    id: 'countXX',
    fn: 'countXX',
    params: ['str'],
    description: 'Count the number of "xx" in the given string. We\'ll say that overlapping is allowed, so "xxx" contains 2 "xx".',
    solution: `function countXX(str) {
  let count = 0;
  for (let i = 0; i < str.length - 1; i++) {
    if (str[i] === 'x' && str[i + 1] === 'x') count++;
  }
  return count;
}`,
    tests: [['abcxx'], ['xxx'], ['xxxx'], ['abc'], ['Hello there'], ['Hexxo thxxe'], [''], ['Kittens'], ['Kittensxxx']],
  },
  {
    id: 'stringBits',
    fn: 'stringBits',
    params: ['str'],
    description: 'Given a string, return a new string made of every other char starting with the first, so "Hello" yields "Hlo".',
    solution: `function stringBits(str) {
  let result = '';
  for (let i = 0; i < str.length; i += 2) result += str[i];
  return result;
}`,
    tests: [['Hello'], ['Hi'], ['Heeololeo'], ['HiHiHi'], [''], ['Greetings'], ['Chocoate'], ['pi'], ['Hello Kitten'], ['hxaxpxpxy']],
  },
  {
    id: 'stringSplosion',
    fn: 'stringSplosion',
    params: ['str'],
    description: 'Given a non-empty string like "Code" return a string like "CCoCodCode".',
    solution: `function stringSplosion(str) {
  let result = '';
  for (let i = 1; i <= str.length; i++) result += str.slice(0, i);
  return result;
}`,
    tests: [['Code'], ['abc'], ['ab'], ['x'], ['fade'], ['There'], ['Kitten'], ['Bye'], ['Good'], ['Bad']],
  },
  {
    id: 'arrayCount9',
    fn: 'arrayCount9',
    params: ['nums'],
    description: 'Given an array of numbers, return the number of 9\'s in the array.',
    solution: `function arrayCount9(nums) {
  return nums.filter((n) => n === 9).length;
}`,
    tests: [[[1, 2, 9]], [[1, 9, 9]], [[1, 9, 9, 3, 9]], [[1, 2, 3]], [[]], [[4, 2, 4, 3, 1]], [[9, 2, 4, 3, 1]]],
  },
  {
    id: 'arrayFront9',
    fn: 'arrayFront9',
    params: ['nums'],
    description: 'Given an array of numbers, return true if one of the first 4 elements in the array is a 9. The array length may be less than 4.',
    solution: `function arrayFront9(nums) {
  return nums.slice(0, 4).includes(9);
}`,
    tests: [[[1, 2, 9, 3, 4]], [[1, 2, 3, 4, 9]], [[1, 2, 3, 4, 5]], [[9, 2, 3]], [[1, 9, 9]], [[1, 2, 3]], [[1, 9]], [[5, 5]], [[2]], [[9]], [[]], [[3, 9, 2, 3, 3]]],
  },
  {
    id: 'array123',
    fn: 'array123',
    params: ['nums'],
    description: 'Given an array of numbers, return true if the sequence of numbers 1, 2, 3 appears in the array somewhere.',
    solution: `function array123(nums) {
  for (let i = 0; i < nums.length - 2; i++) {
    if (nums[i] === 1 && nums[i + 1] === 2 && nums[i + 2] === 3) return true;
  }
  return false;
}`,
    tests: [[[1, 1, 2, 3, 1]], [[1, 1, 2, 4, 1]], [[1, 1, 2, 1, 2, 3]], [[1, 1, 2, 1, 2, 1]], [[1, 2, 3, 1, 2, 3]], [[1, 2, 3]], [[1, 1, 1]], [[1, 2]], [[1]], [[]]],
  },
  {
    id: 'stringMatch',
    fn: 'stringMatch',
    params: ['a', 'b'],
    description: 'Given 2 strings, `a` and `b`, return the number of the positions where they contain the same length 2 substring. So "xxcaazz" and "xxbaaz" yields 3, since the "xx", "aa", and "az" substrings appear in the same place in both strings.',
    solution: `function stringMatch(a, b) {
  const len = Math.min(a.length, b.length);
  let count = 0;
  for (let i = 0; i < len - 1; i++) {
    if (a.slice(i, i + 2) === b.slice(i, i + 2)) count++;
  }
  return count;
}`,
    tests: [['xxcaazz', 'xxbaaz'], ['abc', 'abc'], ['abc', 'axc'], ['hello', 'he'], ['he', 'hello'], ['h', 'hello'], ['', 'hello'], ['aabbccdd', 'abbbxxd'], ['aaxxaaxx', 'iaxxai'], ['iaxxai', 'aaxxaaxx']],
  },
  {
    id: 'noTriples',
    fn: 'noTriples',
    params: ['nums'],
    description: 'Given an array of numbers, we\'ll say that a triple is a value appearing 3 times in a row in the array. Return true if the array does not contain any triples.',
    solution: `function noTriples(nums) {
  for (let i = 0; i < nums.length - 2; i++) {
    if (nums[i] === nums[i + 1] && nums[i] === nums[i + 2]) return false;
  }
  return true;
}`,
    tests: [[[1, 1, 2, 2, 1]], [[1, 1, 2, 2, 2, 1]], [[1, 1, 1, 2, 2, 2, 1]], [[1, 1, 2, 2, 1, 2, 1]], [[1, 2, 1]], [[1, 1, 1]], [[1, 1]], [[1]], [[]]],
  },
];

const string2: Problem[] = [
  {
    id: 'doubleChar',
    fn: 'doubleChar',
    params: ['str'],
    description: 'Given a string, return a string where for every char in the original, there are two chars.',
    solution: `function doubleChar(str) {
  return [...str].map((c) => c + c).join('');
}`,
    tests: [['The'], ['AAbb'], ['Hi-There'], ['Word!'], ['!!'], [''], ['a'], ['.'], ['aa']],
  },
  {
    id: 'countHi',
    fn: 'countHi',
    params: ['str'],
    description: 'Return the number of times that the string "hi" appears anywhere in the given string.',
    solution: `function countHi(str) {
  let count = 0;
  for (let i = 0; i < str.length - 1; i++) {
    if (str.slice(i, i + 2) === 'hi') count++;
  }
  return count;
}`,
    tests: [['abc hi ho'], ['ABChi hi'], ['hihi'], ['hiHIhi'], [''], ['h'], ['hi'], ['Hi is no HI on ahI'], ['hiho not HOHIhi']],
  },
  {
    id: 'catDog',
    fn: 'catDog',
    params: ['str'],
    description: 'Return true if the string "cat" and "dog" appear the same number of times in the given string.',
    solution: `function catDog(str) {
  const count = (word) => str.split(word).length - 1;
  return count('cat') === count('dog');
}`,
    tests: [['catdog'], ['catcat'], ['1cat1cadodog'], ['catxxdogxxxdog'], ['catxdogxdogxcat'], ['catxdogxdogxca'], ['dogdogcat'], ['dogogcat'], ['dog'], ['cat'], ['ca'], ['c'], ['']],
  },
  {
    id: 'endOther',
    fn: 'endOther',
    params: ['a', 'b'],
    description: 'Given two strings, return true if either of the strings appears at the very end of the other string, ignoring upper/lower case differences (in other words, the computation should not be "case sensitive").',
    solution: `function endOther(a, b) {
  const x = a.toLowerCase(), y = b.toLowerCase();
  return x.endsWith(y) || y.endsWith(x);
}`,
    tests: [['Hiabc', 'abc'], ['AbC', 'HiaBc'], ['abc', 'abXabc'], ['Hiabc', 'abcd'], ['Hiabc', 'bc'], ['Hiabcx', 'bc'], ['abc', 'abc'], ['xyz', '12xyz'], ['yz', '12xz'], ['Z', '12xz'], ['12', '12'], ['abcXYZ', 'abcDEF'], ['ab', 'ab12'], ['ab', '12ab']],
  },
  {
    id: 'xyBalance',
    fn: 'xyBalance',
    params: ['str'],
    description: 'We\'ll say that a string is xy-balanced if for all the "x" chars in the string, there exists a "y" char somewhere later in the string. So "xxy" is balanced, but "xyx" is not. One "y" can balance multiple "x"s. Return true if the given string is xy-balanced.',
    solution: `function xyBalance(str) {
  return str.lastIndexOf('y') >= str.lastIndexOf('x');
}`,
    tests: [['aaxbby'], ['aaxbb'], ['yaaxbb'], ['yaaxbby'], ['xaxxbby'], ['xaxxbbyx'], ['xxbxy'], ['xxbx'], ['bbb'], ['bxbb'], ['bxyb'], ['xy'], ['y'], ['x'], [''], ['yxyxyxyx'], ['yxyxyxyxy'], ['12xabxxydxyxyzz']],
  },
  {
    id: 'mixString',
    fn: 'mixString',
    params: ['a', 'b'],
    description: 'Given two strings, `a` and `b`, create a bigger string made of the first char of `a`, the first char of `b`, the second char of `a`, the second char of `b`, and so on. Any leftover chars go at the end of the result.',
    solution: `function mixString(a, b) {
  let result = '';
  const len = Math.max(a.length, b.length);
  for (let i = 0; i < len; i++) {
    if (i < a.length) result += a[i];
    if (i < b.length) result += b[i];
  }
  return result;
}`,
    tests: [['abc', 'xyz'], ['Hi', 'There'], ['xxxx', 'There'], ['xxx', 'X'], ['2/', '27 around'], ['', 'Hello'], ['Abc', ''], ['', ''], ['a', 'b'], ['ax', 'b'], ['a', 'bx'], ['So', 'Long'], ['Long', 'So']],
  },
  {
    id: 'repeatEnd',
    fn: 'repeatEnd',
    params: ['str', 'n'],
    description: 'Given a string and a number `n`, return a string made of `n` repetitions of the last `n` characters of the string. You may assume that `n` is between 0 and the length of the string, inclusive.',
    solution: `function repeatEnd(str, n) {
  return str.slice(str.length - n).repeat(n);
}`,
    tests: [['Hello', 3], ['Hello', 2], ['Hello', 1], ['Hello', 0], ['abc', 3], ['1234', 2], ['1234', 3], ['', 0]],
  },
  {
    id: 'zipZap',
    fn: 'zipZap',
    params: ['str'],
    description: 'Look for patterns like "zip" and "zap" in the string -- length-3, starting with "z" and ending with "p". Return a string where for all such words, the middle letter is gone, so "zipXzap" yields "zpXzp".',
    solution: `function zipZap(str) {
  return str.replace(/z.p/g, 'zp');
}`,
    tests: [['zipXzap'], ['zopzop'], ['zzzopzop'], ['zibzap'], ['zip'], ['zi'], ['z'], [''], ['zzp'], ['abcppp'], ['azbcppp'], ['azbcpzpp']],
  },
];

const array2: Problem[] = [
  {
    id: 'countEvens',
    fn: 'countEvens',
    params: ['nums'],
    description: 'Return the number of even numbers in the given array. Note: the `%` "mod" operator computes the remainder, e.g. `5 % 2` is 1.',
    solution: `function countEvens(nums) {
  return nums.filter((n) => n % 2 === 0).length;
}`,
    tests: [[[2, 1, 2, 3, 4]], [[2, 2, 0]], [[1, 3, 5]], [[]], [[11, 9, 0, 1]], [[2, 11, 9, 0]], [[2]], [[2, 5, 12]]],
  },
  {
    id: 'bigDiff',
    fn: 'bigDiff',
    params: ['nums'],
    description: 'Given an array of numbers length 1 or more, return the difference between the largest and smallest values in the array. Note: `Math.min()` and `Math.max()` accept any number of arguments.',
    solution: `function bigDiff(nums) {
  return Math.max(...nums) - Math.min(...nums);
}`,
    tests: [[[10, 3, 5, 6]], [[7, 2, 10, 9]], [[2, 10, 7, 2]], [[2, 10]], [[10, 2]], [[10, 0]], [[2, 3]], [[2, 2]], [[2]], [[5, 1, 6, 1, 9, 9]], [[7, 6, 8, 5]], [[7, 7, 6, 8, 5, 5, 6]]],
  },
  {
    id: 'centeredAverage',
    fn: 'centeredAverage',
    params: ['nums'],
    description: 'Return the "centered" average of an array of numbers, which we\'ll say is the mean average of the values, except ignoring the largest and smallest values in the array. If there are multiple copies of the smallest value, ignore just one copy, and likewise for the largest value. Use integer division (`Math.floor`) to produce the final average. You may assume that the array is length 3 or more.',
    solution: `function centeredAverage(nums) {
  const sorted = [...nums].sort((a, b) => a - b).slice(1, -1);
  const sum = sorted.reduce((a, b) => a + b, 0);
  return Math.floor(sum / sorted.length);
}`,
    tests: [[[1, 2, 3, 4, 100]], [[1, 1, 5, 5, 10, 8, 7]], [[-10, -4, -2, -4, -2, 0]], [[5, 3, 4, 6, 2]], [[5, 3, 4, 0, 100]], [[100, 0, 5, 3, 4]], [[4, 0, 100]], [[0, 2, 3, 4, 100]], [[1, 1, 100]], [[7, 7, 7]], [[1, 7, 8]], [[1, 1, 99, 99]], [[1000, 0, 1, 99]], [[4, 4, 4, 4, 5]], [[4, 4, 4, 1, 5]], [[6, 4, 8, 12, 3]]],
  },
  {
    id: 'sum13',
    fn: 'sum13',
    params: ['nums'],
    description: 'Return the sum of the numbers in the array, returning 0 for an empty array. Except the number 13 is very unlucky, so it does not count and numbers that come immediately after a 13 also do not count.',
    solution: `function sum13(nums) {
  let sum = 0;
  for (let i = 0; i < nums.length; i++) {
    if (nums[i] === 13 || (i > 0 && nums[i - 1] === 13)) continue;
    sum += nums[i];
  }
  return sum;
}`,
    tests: [[[1, 2, 2, 1]], [[1, 1]], [[1, 2, 2, 1, 13]], [[1, 2, 13, 2, 1, 13]], [[13, 1, 2, 13, 2, 1, 13]], [[]], [[13]], [[13, 13]], [[13, 0, 13]], [[13, 1, 13]], [[5, 7, 2]], [[5, 13, 2]], [[0]], [[13, 0]]],
  },
  {
    id: 'has22',
    fn: 'has22',
    params: ['nums'],
    description: 'Given an array of numbers, return true if the array contains a 2 next to a 2 somewhere.',
    solution: `function has22(nums) {
  return nums.some((n, i) => n === 2 && nums[i + 1] === 2);
}`,
    tests: [[[1, 2, 2]], [[1, 2, 1, 2]], [[2, 1, 2]], [[2, 2, 1, 2]], [[1, 3, 2]], [[1, 3, 2, 2]], [[2, 3, 2, 2]], [[4, 2, 4, 2, 2, 5]], [[1, 2]], [[2, 2]], [[2]], [[]], [[3, 3, 2, 2]], [[5, 2, 5, 2]]],
  },
  {
    id: 'fizzArray',
    fn: 'fizzArray',
    params: ['n'],
    description: 'Given a number `n`, create and return a new array of length `n`, containing the numbers 0, 1, 2, ... n-1. The given `n` may be 0, in which case just return a length 0 array.',
    solution: `function fizzArray(n) {
  return Array.from({ length: n }, (_, i) => i);
}`,
    tests: [[4], [1], [10], [0], [2], [7]],
  },
  {
    id: 'zeroFront',
    fn: 'zeroFront',
    params: ['nums'],
    description: 'Return an array that contains the exact same numbers as the given array, but rearranged so that all the zeros are grouped at the start of the array. Keep the non-zero numbers in their original order.',
    solution: `function zeroFront(nums) {
  return [...nums.filter((n) => n === 0), ...nums.filter((n) => n !== 0)];
}`,
    tests: [[[1, 0, 0, 1]], [[0, 1, 1, 0, 1]], [[1, 0]], [[0, 1]], [[1, 1, 1, 0]], [[2, 2, 2, 2]], [[0, 0, 1, 0]], [[-1, 0, 0, -1, 0]], [[0, -3, 0, -3]], [[]], [[9, 9, 0, 9, 0, 9]]],
  },
  {
    id: 'evenOdd',
    fn: 'evenOdd',
    params: ['nums'],
    description: 'Return an array that contains the exact same numbers as the given array, but rearranged so that all the even numbers come before all the odd numbers. Keep the evens and odds each in their original relative order.',
    solution: `function evenOdd(nums) {
  return [...nums.filter((n) => n % 2 === 0), ...nums.filter((n) => n % 2 !== 0)];
}`,
    tests: [[[1, 0, 1, 0, 0, 1, 1]], [[3, 3, 2]], [[2, 2, 2]], [[3, 2, 2]], [[1, 1, 0, 1, 0]], [[1]], [[1, 2]], [[2, 1]], [[]]],
  },
];

const logic2: Problem[] = [
  {
    id: 'makeBricks',
    fn: 'makeBricks',
    params: ['small', 'big', 'goal'],
    description: 'We want to make a row of bricks that is `goal` inches long. We have a number of small bricks (1 inch each) and big bricks (5 inches each). Return true if it is possible to make the goal by choosing from the given bricks. This is a little harder than it looks and can be done without any loops.',
    solution: `function makeBricks(small, big, goal) {
  const bigUsed = Math.min(big, Math.floor(goal / 5));
  return goal - bigUsed * 5 <= small;
}`,
    tests: [[3, 1, 8], [3, 1, 9], [3, 2, 10], [3, 2, 8], [3, 2, 9], [6, 1, 11], [6, 0, 11], [1, 4, 11], [0, 3, 10], [1, 4, 12], [3, 1, 7], [1, 1, 7], [2, 1, 7], [7, 1, 11], [7, 1, 8], [7, 1, 13], [43, 1, 46], [40, 1, 46], [40, 2, 47], [40, 2, 50], [40, 2, 52], [22, 2, 33], [0, 2, 10], [1000000, 1000, 1000100], [2, 1000000, 100003], [20, 0, 19], [20, 0, 21], [20, 4, 51], [20, 4, 39]],
  },
  {
    id: 'loneSum',
    fn: 'loneSum',
    params: ['a', 'b', 'c'],
    description: 'Given 3 numbers, `a`, `b`, and `c`, return their sum. However, if one of the values is the same as another of the values, it does not count towards the sum.',
    solution: `function loneSum(a, b, c) {
  const vals = [a, b, c];
  return vals.filter((v) => vals.indexOf(v) === vals.lastIndexOf(v)).reduce((s, v) => s + v, 0);
}`,
    tests: [[1, 2, 3], [3, 2, 3], [3, 3, 3], [9, 2, 2], [2, 2, 9], [2, 9, 2], [2, 9, 3], [4, 2, 3], [1, 3, 1]],
  },
  {
    id: 'luckySum',
    fn: 'luckySum',
    params: ['a', 'b', 'c'],
    description: 'Given 3 numbers, `a`, `b`, and `c`, return their sum. However, if one of the values is 13 then it does not count towards the sum and values to its right do not count. So for example, if `b` is 13, then both `b` and `c` do not count.',
    solution: `function luckySum(a, b, c) {
  let sum = 0;
  for (const v of [a, b, c]) {
    if (v === 13) break;
    sum += v;
  }
  return sum;
}`,
    tests: [[1, 2, 3], [1, 2, 13], [1, 13, 3], [1, 13, 13], [6, 5, 2], [13, 2, 3], [13, 2, 13], [13, 13, 2], [9, 4, 13], [8, 13, 2], [7, 2, 1], [3, 3, 13]],
  },
  {
    id: 'noTeenSum',
    fn: 'noTeenSum',
    params: ['a', 'b', 'c'],
    description: 'Given 3 numbers, `a`, `b`, and `c`, return their sum. However, if any of the values is a teen -- in the range 13..19 inclusive -- then that value counts as 0, except 15 and 16 do not count as teens.',
    solution: `function noTeenSum(a, b, c) {
  const fix = (n) => (n >= 13 && n <= 19 && n !== 15 && n !== 16 ? 0 : n);
  return fix(a) + fix(b) + fix(c);
}`,
    tests: [[1, 2, 3], [2, 13, 1], [2, 1, 14], [2, 1, 15], [2, 1, 16], [2, 1, 17], [17, 1, 2], [2, 15, 2], [16, 17, 18], [17, 18, 19], [15, 16, 1], [15, 15, 19], [15, 19, 16], [5, 17, 18], [17, 18, 16], [17, 19, 18]],
  },
  {
    id: 'roundSum',
    fn: 'roundSum',
    params: ['a', 'b', 'c'],
    description: 'For this problem, we\'ll round a number value up to the next multiple of 10 if its rightmost digit is 5 or more, so 15 rounds up to 20. Alternately, round down to the previous multiple of 10 if its rightmost digit is less than 5, so 12 rounds down to 10. Given 3 non-negative numbers, `a`, `b`, `c`, return the sum of their rounded values.',
    solution: `function roundSum(a, b, c) {
  const round10 = (n) => (n % 10 >= 5 ? n + 10 - (n % 10) : n - (n % 10));
  return round10(a) + round10(b) + round10(c);
}`,
    tests: [[16, 17, 18], [12, 13, 14], [6, 4, 4], [4, 6, 5], [4, 4, 6], [9, 4, 4], [0, 0, 1], [0, 9, 0], [10, 10, 19], [20, 30, 40], [45, 21, 30], [23, 11, 26], [23, 24, 25], [25, 24, 25], [23, 24, 29], [11, 24, 36], [24, 36, 32], [14, 12, 26], [12, 10, 24]],
  },
  {
    id: 'closeFar',
    fn: 'closeFar',
    params: ['a', 'b', 'c'],
    description: 'Given three numbers, `a` `b` `c`, return true if one of `b` or `c` is "close" (differing from `a` by at most 1), while the other is "far", differing from both other values by 2 or more.',
    solution: `function closeFar(a, b, c) {
  const close = (x, y) => Math.abs(x - y) <= 1;
  const far = (x, y) => Math.abs(x - y) >= 2;
  return (close(a, b) && far(a, c) && far(b, c)) || (close(a, c) && far(a, b) && far(c, b));
}`,
    tests: [[1, 2, 10], [1, 2, 3], [4, 1, 3], [4, 5, 3], [4, 3, 5], [-1, 10, 0], [0, -1, 10], [10, 10, 8], [10, 8, 9], [8, 9, 10], [8, 9, 7], [8, 6, 9]],
  },
  {
    id: 'blackjack',
    fn: 'blackjack',
    params: ['a', 'b'],
    description: 'Given 2 positive numbers, return whichever value is nearest to 21 without going over. Return 0 if they both go over.',
    solution: `function blackjack(a, b) {
  if (a > 21 && b > 21) return 0;
  if (a > 21) return b;
  if (b > 21) return a;
  return Math.max(a, b);
}`,
    tests: [[19, 21], [21, 19], [19, 22], [22, 19], [22, 50], [22, 22], [33, 1], [1, 2], [34, 33], [17, 19], [18, 17], [16, 23], [3, 4], [3, 2], [21, 20]],
  },
  {
    id: 'makeChocolate',
    fn: 'makeChocolate',
    params: ['small', 'big', 'goal'],
    description: 'We want to make a package of `goal` kilos of chocolate. We have small bars (1 kilo each) and big bars (5 kilos each). Return the number of small bars to use, assuming we always use big bars before small bars. Return -1 if it can\'t be done.',
    solution: `function makeChocolate(small, big, goal) {
  const bigUsed = Math.min(big, Math.floor(goal / 5));
  const needed = goal - bigUsed * 5;
  return needed <= small ? needed : -1;
}`,
    tests: [[4, 1, 9], [4, 1, 10], [4, 1, 7], [6, 2, 7], [4, 1, 5], [4, 1, 4], [5, 4, 9], [9, 3, 18], [3, 1, 9], [1, 2, 7], [1, 2, 6], [1, 2, 5], [6, 1, 10], [6, 1, 11], [6, 1, 12], [6, 1, 13], [6, 2, 10], [6, 2, 11], [6, 2, 12], [60, 100, 550], [1000, 1000000, 5000006], [7, 1, 12], [7, 1, 13], [7, 2, 13]],
  },
];

const functional1: Problem[] = [
  {
    id: 'doubling',
    fn: 'doubling',
    params: ['nums'],
    description: 'Given an array of numbers, return a new array where each number is multiplied by 2. Try using `.map()`.',
    solution: `function doubling(nums) {
  return nums.map((n) => n * 2);
}`,
    tests: [[[1, 2, 3]], [[6, 8, 6, 8, -1]], [[]], [[5]], [[5, 10]], [[8, -5, 7, 3, 109]], [[6, -3, 12, 23, 4, 1, 19, 11, 2, 3, 2]], [[3, 1, 4, 1, 5, 9]]],
  },
  {
    id: 'square',
    fn: 'square',
    params: ['nums'],
    description: 'Given an array of numbers, return a new array where each number is squared.',
    solution: `function square(nums) {
  return nums.map((n) => n * n);
}`,
    tests: [[[1, 2, 3]], [[6, 8, -6, -8, 1]], [[]], [[12]], [[5, 10]], [[6, -3, 12, 23, 4, 1, 19, 11, 2, 3, 2]]],
  },
  {
    id: 'addStar',
    fn: 'addStar',
    params: ['strings'],
    description: 'Given an array of strings, return a new array where each string has "*" added at its end.',
    solution: `function addStar(strings) {
  return strings.map((s) => s + '*');
}`,
    tests: [[['a', 'bb', 'ccc']], [['hello', 'there']], [['*']], [[]], [['kittens', 'and', 'chocolate', 'and']], [['empty', '']]],
  },
  {
    id: 'lower',
    fn: 'lower',
    params: ['strings'],
    description: 'Given an array of strings, return a new array where each string is converted to lower case.',
    solution: `function lower(strings) {
  return strings.map((s) => s.toLowerCase());
}`,
    tests: [[['Hello', 'Hi']], [['AAA', 'BBB', 'ccc']], [['KitteN', 'ChocolaTE']], [[]], [['EMPTY', '']]],
  },
  {
    id: 'noNeg',
    fn: 'noNeg',
    params: ['nums'],
    description: 'Given an array of numbers, return a new array of the numbers, omitting any that are less than 0. Try using `.filter()`.',
    solution: `function noNeg(nums) {
  return nums.filter((n) => n >= 0);
}`,
    tests: [[[1, -2]], [[-3, -3, 3, 3]], [[-1, -1, -1]], [[]], [[0, 1, 2]], [[3, -10, 1, -1, 4, -400]], [[-1, 3, 1, -1, -10, -100, -111, 5]]],
  },
  {
    id: 'noLong',
    fn: 'noLong',
    params: ['strings'],
    description: 'Given an array of strings, return a new array omitting any string length 4 or more.',
    solution: `function noLong(strings) {
  return strings.filter((s) => s.length < 4);
}`,
    tests: [[['this', 'not', 'too', 'long']], [['a', 'bbb', 'cccc']], [['cccc', 'cccc', 'cccc']], [[]], [['']], [['empty', '', 'empty']], [['a']], [['aaaa', 'bbb', '***', '333']]],
  },
  {
    id: 'sumAll',
    fn: 'sumAll',
    params: ['nums'],
    description: 'Given an array of numbers, return the sum of all the numbers. Return 0 for an empty array. Try using `.reduce()`.',
    solution: `function sumAll(nums) {
  return nums.reduce((acc, n) => acc + n, 0);
}`,
    tests: [[[1, 2, 3]], [[]], [[5]], [[-1, 1]], [[10, 20, 30, 40]], [[3, -7, 12]]],
  },
  {
    id: 'countWords',
    fn: 'countWords',
    params: ['words'],
    description: 'Given an array of strings, return an object where each key is a string from the array and its value is the number of times it appears. e.g. `["a", "b", "a"]` yields `{ a: 2, b: 1 }`.',
    solution: `function countWords(words) {
  const counts = {};
  for (const w of words) counts[w] = (counts[w] || 0) + 1;
  return counts;
}`,
    tests: [[['a', 'b', 'a', 'c', 'b']], [['c', 'b', 'a']], [['c', 'c', 'c', 'c']], [[]], [['this', 'and', 'this', '']], [['x', 'y', 'x', 'Y', 'X']]],
  },
];

const recursion1: Problem[] = [
  {
    id: 'factorial',
    fn: 'factorial',
    params: ['n'],
    description: 'Given `n` of 1 or more, return the factorial of `n`, which is n * (n-1) * (n-2) ... 1. Compute the result recursively (without loops).',
    solution: `function factorial(n) {
  return n <= 1 ? 1 : n * factorial(n - 1);
}`,
    tests: [[1], [2], [3], [4], [5], [6], [7], [8], [12]],
  },
  {
    id: 'bunnyEars',
    fn: 'bunnyEars',
    params: ['bunnies'],
    description: 'We have a number of bunnies and each bunny has two big floppy ears. We want to compute the total number of ears across all the bunnies recursively (without loops or multiplication).',
    solution: `function bunnyEars(bunnies) {
  return bunnies === 0 ? 0 : 2 + bunnyEars(bunnies - 1);
}`,
    tests: [[0], [1], [2], [3], [4], [5], [12], [50], [234]],
  },
  {
    id: 'fibonacci',
    fn: 'fibonacci',
    params: ['n'],
    description: 'The fibonacci sequence is a famous bit of mathematics. The first two values in the sequence are 0 and 1 (essentially 2 base cases). Each subsequent value is the sum of the previous two values, so the whole sequence is: 0, 1, 1, 2, 3, 5, 8, 13, 21 and so on. Define a recursive `fibonacci(n)` that returns the nth fibonacci number, with n=0 representing the start of the sequence.',
    solution: `function fibonacci(n) {
  if (n < 2) return n;
  return fibonacci(n - 1) + fibonacci(n - 2);
}`,
    tests: [[0], [1], [2], [3], [4], [5], [6], [7], [15]],
  },
  {
    id: 'triangle',
    fn: 'triangle',
    params: ['rows'],
    description: 'We have triangle made of blocks. The topmost row has 1 block, the next row down has 2 blocks, the next row has 3 blocks, and so on. Compute recursively (no loops or multiplication) the total number of blocks in such a triangle with the given number of rows.',
    solution: `function triangle(rows) {
  return rows === 0 ? 0 : rows + triangle(rows - 1);
}`,
    tests: [[0], [1], [2], [3], [4], [5], [6], [7]],
  },
  {
    id: 'sumDigits',
    fn: 'sumDigits',
    params: ['n'],
    description: 'Given a non-negative number `n`, return the sum of its digits recursively (no loops). Note that mod (`%`) by 10 yields the rightmost digit (126 % 10 is 6), while dividing by 10 and flooring removes the rightmost digit (`Math.floor(126 / 10)` is 12).',
    solution: `function sumDigits(n) {
  return n < 10 ? n : (n % 10) + sumDigits(Math.floor(n / 10));
}`,
    tests: [[126], [49], [12], [10], [1], [0], [730], [1111], [11111], [10110], [235]],
  },
  {
    id: 'count7',
    fn: 'count7',
    params: ['n'],
    description: 'Given a non-negative number `n`, return the count of the occurrences of 7 as a digit, so for example 717 yields 2. (no loops).',
    solution: `function count7(n) {
  if (n === 0) return 0;
  return (n % 10 === 7 ? 1 : 0) + count7(Math.floor(n / 10));
}`,
    tests: [[717], [7], [123], [77], [7123], [771237], [771737], [47571], [777777], [70701277], [777576197], [99999], [99799]],
  },
  {
    id: 'changeXY',
    fn: 'changeXY',
    params: ['str'],
    description: 'Given a string, compute recursively (no loops) a new string where all the lowercase "x" chars have been changed to "y" chars.',
    solution: `function changeXY(str) {
  if (str.length === 0) return '';
  return (str[0] === 'x' ? 'y' : str[0]) + changeXY(str.slice(1));
}`,
    tests: [['codex'], ['xxhixx'], ['xhixhix'], ['hiy'], ['h'], ['x'], [''], ['xxx'], ['yyhxyi'], ['hihi']],
  },
  {
    id: 'array6',
    fn: 'array6',
    params: ['nums', 'index'],
    description: 'Given an array of numbers, compute recursively if the array contains a 6. We\'ll use the convention of considering only the part of the array that begins at the given index. In this way, a recursive call can pass `index + 1` to move down the array. The initial call will pass in index as 0.',
    solution: `function array6(nums, index) {
  if (index >= nums.length) return false;
  return nums[index] === 6 || array6(nums, index + 1);
}`,
    tests: [[[1, 6, 4], 0], [[1, 4], 0], [[6], 0], [[], 0], [[6, 2, 2], 0], [[2, 5], 0], [[1, 9, 4, 6, 6], 0], [[2, 5, 6], 0]],
  },
  {
    id: 'pairStar',
    fn: 'pairStar',
    params: ['str'],
    description: 'Given a string, compute recursively a new string where identical chars that are adjacent in the original string are separated from each other by a "*".',
    solution: `function pairStar(str) {
  if (str.length < 2) return str;
  const sep = str[0] === str[1] ? '*' : '';
  return str[0] + sep + pairStar(str.slice(1));
}`,
    tests: [['hello'], ['xxyy'], ['aaaa'], ['aaab'], ['aa'], ['a'], [''], ['noadjacent'], ['abba'], ['abbba']],
  },
];

export const CATEGORIES: Category[] = [
  { id: 'Warmup-1', name: 'Warmup-1', level: 1, blurb: 'Simple warmup problems to get started (solutions available).', problems: warmup1 },
  { id: 'String-1', name: 'String-1', level: 1, blurb: 'Basic string problems -- no loops.', problems: string1 },
  { id: 'Array-1', name: 'Array-1', level: 1, blurb: 'Basic array problems -- no loops.', problems: array1 },
  { id: 'Logic-1', name: 'Logic-1', level: 1, blurb: 'Basic boolean logic puzzles -- if else && || !', problems: logic1 },
  { id: 'Warmup-2', name: 'Warmup-2', level: 2, blurb: 'Medium warmup string/array loops.', problems: warmup2 },
  { id: 'String-2', name: 'String-2', level: 2, blurb: 'Medium string problems -- 1 loop.', problems: string2 },
  { id: 'Array-2', name: 'Array-2', level: 2, blurb: 'Medium array problems -- 1 loop.', problems: array2 },
  { id: 'Logic-2', name: 'Logic-2', level: 2, blurb: 'Medium boolean logic puzzles -- if else && || !', problems: logic2 },
  { id: 'Functional-1', name: 'Functional-1', level: 2, blurb: 'Functional array methods: map, filter, reduce.', problems: functional1 },
  { id: 'Recursion-1', name: 'Recursion-1', level: 3, blurb: 'Basic recursion problems.', problems: recursion1 },
];

export interface ProblemRef {
  problem: Problem;
  category: Category;
  index: number;
}

export function findProblem(categoryId: string, problemId: string): ProblemRef | undefined {
  const category = CATEGORIES.find((c) => c.id === categoryId);
  if (!category) return undefined;
  const index = category.problems.findIndex((p) => p.id === problemId);
  if (index === -1) return undefined;
  return { problem: category.problems[index], category, index };
}

export function starterCode(p: Problem): string {
  return `function ${p.fn}(${p.params.join(', ')}) {\n  \n}\n`;
}

export const TOTAL_PROBLEMS = CATEGORIES.reduce((n, c) => n + c.problems.length, 0);
